import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import ProjectFinalApprovalApi from "../../../../../infrastructure/api/projectFinalApproval";
import { toJalaliFullDate } from "../../../../middleware/middleware";

const formatPrice = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return num.toLocaleString("fa-IR");
};

const statusColor = (statusTitle) => {
  const text = String(statusTitle || "");
  if (text.includes("آماده اجرا")) return "success";
  if (text.includes("رد")) return "error";
  if (text.includes("انتظار")) return "warning";
  return "default";
};

const normalizePendingItem = (raw) => ({
  projectFinalApprovalId: raw?.projectFinalApprovalId ?? raw?.ProjectFinalApprovalId,
  projectContractId: raw?.projectContractId ?? raw?.ProjectContractId,
  requestId: raw?.requestId ?? raw?.RequestId,
  requestReferenceId: raw?.requestReferenceId ?? raw?.RequestReferenceId ?? "",
  requestTitle: raw?.requestTitle ?? raw?.RequestTitle ?? "",
  projectQuoteId: raw?.projectQuoteId ?? raw?.ProjectQuoteId,
  revisionNo: raw?.revisionNo ?? raw?.RevisionNo ?? 0,
  price: raw?.price ?? raw?.Price ?? 0,
  contractTitle: raw?.contractTitle ?? raw?.ContractTitle ?? "",
  contractFileUrl: raw?.contractFileUrl ?? raw?.ContractFileUrl ?? "",
  projectContractStatusTitle:
    raw?.projectContractStatusTitle ?? raw?.ProjectContractStatusTitle ?? "",
  contractorTitle: raw?.contractorTitle ?? raw?.ContractorTitle ?? "",
  contractorCompanyName: raw?.contractorCompanyName ?? raw?.ContractorCompanyName ?? "",
  hasContractorAcceptance:
    raw?.hasContractorAcceptance ?? raw?.HasContractorAcceptance ?? false,
  contractorAcceptedAt: raw?.contractorAcceptedAt ?? raw?.ContractorAcceptedAt,
  approvalStatusTitle: raw?.approvalStatusTitle ?? raw?.ApprovalStatusTitle ?? "",
});

const ProjectFinalApprovalPage = ({ source = "Management" }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [approveDialog, setApproveDialog] = useState({ open: false, item: null });
  const [rejectDialog, setRejectDialog] = useState({ open: false, item: null });
  const [approveForm, setApproveForm] = useState({
    technicalApproval: false,
    financialApproval: false,
    legalApproval: false,
    notes: "",
  });
  const [rejectReason, setRejectReason] = useState("");
  const [rejectNotes, setRejectNotes] = useState("");

  const areaLabel = source === "Management" ? "مدیریت" : "اپراتور سولارنت";
  const basePath =
    source === "Management" ? "/SLN/Management" : "/SLN/SolarentOperator";

  const loadItems = async () => {
    setLoading(true);
    setError("");
    const res = await ProjectFinalApprovalApi.getPendingFinalApprovals(source);
    if (!res.ok) {
      setError(res.message || "خطا در دریافت لیست تایید نهایی");
      setItems([]);
    } else {
      const list = Array.isArray(res.data) ? res.data : [];
      setItems(list.map(normalizePendingItem));
    }
    setLoading(false);
  };

  useEffect(() => {
    loadItems();
  }, [source]);

  const openApprove = (item) => {
    setApproveForm({
      technicalApproval: false,
      financialApproval: false,
      legalApproval: false,
      notes: "",
    });
    setApproveDialog({ open: true, item });
    setError("");
    setSuccess("");
  };

  const openReject = (item) => {
    setRejectReason("");
    setRejectNotes("");
    setRejectDialog({ open: true, item });
    setError("");
    setSuccess("");
  };

  const handleApprove = async () => {
    const item = approveDialog.item;
    if (!item?.projectContractId) return;

    if (
      !approveForm.technicalApproval ||
      !approveForm.financialApproval ||
      !approveForm.legalApproval
    ) {
      setError("برای تایید نهایی، هر سه مورد فنی، مالی و حقوقی باید تیک بخورند.");
      return;
    }

    setSubmitting(true);
    setError("");
    const res = await ProjectFinalApprovalApi.approveProjectFinal(source, {
      projectContractId: item.projectContractId,
      technicalApproval: approveForm.technicalApproval,
      financialApproval: approveForm.financialApproval,
      legalApproval: approveForm.legalApproval,
      notes: approveForm.notes,
    });

    if (!res.ok) {
      setError(res.message || "تایید نهایی ناموفق بود.");
    } else {
      const statusTitle = res.data?.projectContractStatusTitle;
      setSuccess(
        statusTitle
          ? `${res.message || "پروژه تایید نهایی شد."} — وضعیت: ${statusTitle}`
          : res.message || "پروژه با موفقیت تایید نهایی شد."
      );
      setApproveDialog({ open: false, item: null });
      await loadItems();
    }
    setSubmitting(false);
  };

  const handleReject = async () => {
    const item = rejectDialog.item;
    if (!item?.projectContractId) return;
    if (!rejectReason.trim()) {
      setError("دلیل رد یا نیاز به اصلاح الزامی است.");
      return;
    }

    setSubmitting(true);
    setError("");
    const res = await ProjectFinalApprovalApi.rejectProjectFinal(source, {
      projectContractId: item.projectContractId,
      rejectionReason: rejectReason.trim(),
      notes: rejectNotes.trim(),
    });

    if (!res.ok) {
      setError(res.message || "ثبت رد تایید نهایی ناموفق بود.");
    } else {
      setSuccess(res.message || "رد تایید نهایی ثبت شد.");
      setRejectDialog({ open: false, item: null });
      await loadItems();
    }
    setSubmitting(false);
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <BreadcrumbsComponent
          items={[
            { title: "SLN", path: "/SLN" },
            { title: areaLabel, path: basePath },
            { title: "تایید نهایی پروژه‌ها" },
          ]}
        />
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardHeader
            title="تایید نهایی پروژه‌ها"
            subheader="پروژه‌های legacy (پذیرش پیمانکار) و پروژه‌های مسیر جدید (قیمت مشتری + قرارداد اجرا) که منتظر تایید نهایی سولارنت هستند."
            action={
              <IconButton onClick={loadItems} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            }
          />
          <CardContent>
            {error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : null}
            {success ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            ) : null}

            {!loading && items.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                اگر پرونده در workspace در مرحله «تایید نهایی و اجرا» است، تایید را
                مستقیماً از همان بخش (پنل «تایید نهایی پروژه») انجام دهید. پس از
                restart سرور API، پرونده‌های مسیر جدید در این لیست هم نمایش داده
                می‌شوند.
              </Alert>
            ) : null}

            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>درخواست</TableCell>
                    <TableCell>پیمانکار</TableCell>
                    <TableCell>پیشنهاد / قیمت</TableCell>
                    <TableCell>مبلغ</TableCell>
                    <TableCell>پذیرش / قرارداد مشتری</TableCell>
                    <TableCell>وضعیت قرارداد</TableCell>
                    <TableCell align="center">عملیات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        {loading
                          ? "در حال بارگذاری..."
                          : "پروژه‌ای در انتظار تایید نهایی نیست."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((item) => (
                      <TableRow key={item.projectContractId}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {item.requestTitle || `درخواست ${item.requestId}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            کد پیگیری: {item.requestReferenceId || item.requestId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {item.contractorTitle || item.contractorCompanyName || "-"}
                          </Typography>
                          {item.contractorCompanyName &&
                          item.contractorCompanyName !== item.contractorTitle ? (
                            <Typography variant="caption" color="text.secondary">
                              {item.contractorCompanyName}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          {item.projectQuoteId ? (
                            <>
                              #{item.projectQuoteId}
                              {item.revisionNo ? ` / نسخه ${item.revisionNo}` : ""}
                            </>
                          ) : item.hasContractorAcceptance ? (
                            "قیمت مشتری"
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>{formatPrice(item.price)} ریال</TableCell>
                        <TableCell>
                          {item.hasContractorAcceptance ? (
                            <Stack spacing={0.5}>
                              <Chip
                                size="small"
                                color="success"
                                label={
                                  item.contractorAcceptedAt
                                    ? "پذیرش پیمانکار"
                                    : "قرارداد مشتری"
                                }
                              />
                              {item.contractorAcceptedAt ? (
                                <Typography variant="caption" color="text.secondary">
                                  {toJalaliFullDate(item.contractorAcceptedAt) || "-"}
                                </Typography>
                              ) : null}
                            </Stack>
                          ) : (
                            <Chip size="small" color="warning" label="ثبت نشده" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={statusColor(item.projectContractStatusTitle)}
                            label={item.projectContractStatusTitle || "-"}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircleOutlineIcon />}
                              disabled={loading || submitting}
                              onClick={() => openApprove(item)}
                            >
                              تایید نهایی
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              startIcon={<CancelOutlinedIcon />}
                              disabled={loading || submitting}
                              onClick={() => openReject(item)}
                            >
                              رد / نیاز به اصلاح
                            </Button>
                          </Stack>
                          {item.contractFileUrl ? (
                            <Box sx={{ mt: 1 }}>
                              <Link href={item.contractFileUrl} target="_blank" rel="noopener">
                                مشاهده قرارداد
                              </Link>
                            </Box>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Paper>
          </CardContent>
        </Card>
      </Grid>

      <Dialog
        open={approveDialog.open}
        onClose={() => setApproveDialog({ open: false, item: null })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>تایید نهایی پروژه</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            درخواست: {approveDialog.item?.requestTitle || "-"}
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={approveForm.technicalApproval}
                  onChange={(e) =>
                    setApproveForm((prev) => ({
                      ...prev,
                      technicalApproval: e.target.checked,
                    }))
                  }
                />
              }
              label="تایید فنی"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={approveForm.financialApproval}
                  onChange={(e) =>
                    setApproveForm((prev) => ({
                      ...prev,
                      financialApproval: e.target.checked,
                    }))
                  }
                />
              }
              label="تایید مالی"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={approveForm.legalApproval}
                  onChange={(e) =>
                    setApproveForm((prev) => ({
                      ...prev,
                      legalApproval: e.target.checked,
                    }))
                  }
                />
              }
              label="تایید حقوقی"
            />
          </FormGroup>
          <TextField
            fullWidth
            multiline
            minRows={2}
            sx={{ mt: 2 }}
            label="یادداشت"
            value={approveForm.notes}
            onChange={(e) =>
              setApproveForm((prev) => ({ ...prev, notes: e.target.value }))
            }
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            پس از تایید، وضعیت پروژه به «آماده اجرا» تغییر می‌کند. هنوز هیچ
            پرداخت یا زمان‌بندی اجرایی ایجاد نمی‌شود.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog({ open: false, item: null })}>
            انصراف
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={submitting}
            onClick={handleApprove}
          >
            تایید نهایی
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rejectDialog.open}
        onClose={() => setRejectDialog({ open: false, item: null })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>رد / نیاز به اصلاح</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            required
            multiline
            minRows={3}
            label="دلیل رد یا نیاز به اصلاح"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="یادداشت"
            value={rejectNotes}
            onChange={(e) => setRejectNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, item: null })}>
            انصراف
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={submitting}
            onClick={handleReject}
          >
            ثبت رد
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default ProjectFinalApprovalPage;
